import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect } from "react";
import { useMediaQuery } from "react-responsive";
import classNames from "classnames";
import dompurify from "isomorphic-dompurify";
import { useMessageBus } from "@jti/fe-message-bus";
import HealthBannerLogo from "./HealthBannerLogo";
import { setElementPositionForBanner, setStickyBanner, setTopPositionBanner, waitForDynamicElementAndApplySpacing, } from "./helpers";
import "./HealthBanner.scss";
const { sanitize } = dompurify;
const HealthBanner = ({ content, logo, alt, src, position, font, fontColor, backgroundColor, bannerSize, bordersColor, bordersSize, isSticky, isDefaultFont, fontSize, fontSizePercent, isRounded, isAllowedToExpand, shouldLimitHeight, }) => {
    const messageBus = useMessageBus();
    const MEDIUM_WIDTH = 960;
    const SMALL_WIDTH = 765;
    const isLargeBreakpoint = useMediaQuery({ query: `(min-width: ${MEDIUM_WIDTH}px)` });
    const isBelowDesktopBreakpoint = useMediaQuery({
        query: `(min-width: ${SMALL_WIDTH}px) and (max-width: ${MEDIUM_WIDTH - 1}px)`,
    });
    const isSmallBreakpoint = useMediaQuery({ query: `(max-width: ${SMALL_WIDTH}px)` });
    const height = `${bannerSize}vh`;
    const isStickyBanner = isSticky.toLowerCase() === "true";
    const isDefaultFontSize = isDefaultFont?.toLowerCase() === "true";
    const isTop = position === "top";
    const style = {
        position: isStickyBanner ? "fixed" : "static",
        bottom: !isTop ? "0" : undefined,
        top: isTop ? "0" : undefined,
        height: shouldLimitHeight && !isAllowedToExpand ? height : undefined,
        minHeight: shouldLimitHeight && isAllowedToExpand ? height : undefined,
        backgroundColor,
        color: fontColor,
        fontFamily: font,
        fontSize: isDefaultFontSize ? `${fontSize}px` : `calc(${height} / 100 * ${fontSizePercent})`,
        border: `${bordersSize}px solid ${bordersColor}`,
        marginTop: isTop && !isStickyBanner ? "-15px" : "",
    };
    const currentClassName = classNames("healthbanner__wrapper", {
        "healthbanner__wrapper--sticky-top": isTop && isStickyBanner,
        "healthbanner__wrapper--rounded": isRounded,
        "healthbanner__wrapper--sticky": isStickyBanner,
    });
    const applySpacingForDynamicElement = (dynamicElementSelector, value, property = "paddingBottom") => {
        const observer = new MutationObserver((_mutationsList, observer) => {
            const dynamicElement = window.document.querySelector(dynamicElementSelector);
            if (dynamicElement) {
                setElementPositionForBanner({
                    selector: dynamicElementSelector,
                    value: `${value}`,
                    property,
                });
                observer.disconnect();
            }
        });
        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });
    };
    useEffect(() => {
        window.document.body.classList.add("body--health-banner");
        const isHeaderTransparent = window.document
            .getElementsByClassName("header__container")[0]
            ?.classList.contains("header__container--overlay");
        const isInfoBanner = document.body.classList.contains("body--info-banner");
        setElementPositionForBanner({
            selector: ".navigation__list-container",
            value: height,
            property: "paddingBottom",
        });
        if (isStickyBanner) {
            setStickyBanner(isTop, height);
        }
        if (isTop && (isStickyBanner || (!isStickyBanner && isHeaderTransparent))) {
            setTopPositionBanner(height);
        }
        else if (!isInfoBanner) {
            setTopPositionBanner("");
        }
        if (isTop && !isLargeBreakpoint) {
            setElementPositionForBanner({
                selector: ".headerlinks__listItem--account .aem-account-dropdown",
                value: height,
                property: "marginTop",
            });
            if (isStickyBanner && isSmallBreakpoint) {
                setElementPositionForBanner({
                    selector: ".aem-anchorlinks__container",
                    value: height,
                    property: "marginTop",
                });
            }
        }
        if (!isTop && (isBelowDesktopBreakpoint || isSmallBreakpoint) && isStickyBanner) {
            applySpacingForDynamicElement('.aem-store-locator button[class*="applyButton"]', height, "marginBottom");
        }
        messageBus.subscribe({
            channel: "cart.loaded",
            callback: () => {
                if (!isTop && isStickyBanner) {
                    setElementPositionForBanner({
                        selector: "[data-js-selector=mini-cart-content]",
                        value: height,
                        property: "marginBottom",
                    });
                }
            },
        });
        return () => {
            messageBus.unsubscribe("cart.loaded");
        };
    }, [height, isLargeBreakpoint, isStickyBanner, isTop, isSmallBreakpoint, isBelowDesktopBreakpoint, messageBus]);
    useEffect(() => {
        const isProductPage = window.document.body.classList.contains("productpage");
        if (!isProductPage || isTop)
            return;
        if (!isStickyBanner) {
            if (!isLargeBreakpoint) {
                waitForDynamicElementAndApplySpacing(".js-productDetails__ctaContainer", "#doc");
            }
            else {
                setElementPositionForBanner({
                    selector: "#doc",
                    value: "0",
                    property: "paddingBottom",
                });
            }
        }
        else {
            if (!isLargeBreakpoint) {
                applySpacingForDynamicElement(".js-productDetails__ctaContainer", height, "marginBottom");
            }
        }
    }, [height, isLargeBreakpoint, isStickyBanner, isTop]);
    return (_jsxs("div", { className: currentClassName, style: style, children: [content && (_jsx("p", { className: "healthbanner__message", dangerouslySetInnerHTML: {
                    __html: sanitize(content),
                } })), _jsx(HealthBannerLogo, { logo: logo, src: src, alt: alt })] }));
};
export default HealthBanner;
//# sourceMappingURL=HealthBanner.js.map